import { useEffect, useId, useRef, useState } from "react";
import { AsYouType, getCountryCallingCode } from "libphonenumber-js";
import styles from "./index.module.css";
import CountrySelect from "./CountrySelect";
import { detectInitialCountrySync } from "./detectCountry";
import {
  capNationalDigits,
  formatNational,
  getPhoneError,
  sanitizeInput,
  splitDigits,
  toE164,
} from "./validate";

// Value/onChange keep the wire format this component has always used:
// calling code + national number concatenated, no "+", no separators —
// what react-phone-input-2's onChange used to hand back. DataForm and
// IntesivePage/Form both build the E.164 string themselves from that
// (`\`+${phoneNumber}\``), so this component doesn't change their contract.
export default function PhoneInput({
  value = "",
  onChange,
  country: countryProp,
  defaultCountry = "UA",
  preferredCountries,
  onCountryChange,
  label,
  placeholder,
  description,
  error,
  required,
  disabled,
  readOnly,
  loading,
  grey,
  className,
  onBlur,
  onFocus,
}) {
  const [country, setCountry] = useState(() =>
    detectInitialCountrySync({ value, country: countryProp, defaultCountry }),
  );
  const [nationalDigits, setNationalDigits] = useState(
    () => splitDigits(value, country).nationalDigits,
  );
  // Sanitized "+…" text (a plus and digits, nothing else) kept verbatim
  // while a pasted/typed international number is too short for
  // libphonenumber-js to tell which country it belongs to yet (e.g. just
  // "+3") — there's nothing to split into country/national parts until
  // then, so the keystroke would otherwise vanish from the field.
  const [pendingInternational, setPendingInternational] = useState(null);
  const [touched, setTouched] = useState(false);

  const countryManuallySet = useRef(Boolean(countryProp) || Boolean(value));
  const lastEmitted = useRef(value ?? "");
  const numberInputRef = useRef(null);

  const inputId = useId();
  const descId = useId();
  const errorId = useId();

  // Re-syncs from a genuinely external `value` change (e.g. the parent form
  // resetting) — but only then. Echoes of our own onChange are recognized
  // via `lastEmitted` and ignored, so normal typing never fights this.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value ?? "";
    setPendingInternational(null);

    if (!value) {
      setNationalDigits("");
      return;
    }

    countryManuallySet.current = true;
    const parsed = parseForCountry(value, country);
    const nextCountry = parsed || country;
    setCountry(nextCountry);
    setNationalDigits(splitDigits(value, nextCountry).nationalDigits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function parseForCountry(digits, fallbackCountry) {
    const ayt = new AsYouType();
    ayt.input(toE164(digits));
    return ayt.getCountry() || fallbackCountry;
  }

  function emit({ nextCountry = country, nextNationalDigits = nationalDigits, nextPending = null }) {
    setPendingInternational(nextPending);
    setCountry(nextCountry);
    setNationalDigits(nextNationalDigits);

    const digits = nextPending
      ? nextPending.replace(/\D/g, "")
      : `${getCountryCallingCode(nextCountry)}${nextNationalDigits}`;
    lastEmitted.current = digits;
    onChange?.(digits);
  }

  function handleNumberChange(event) {
    // Never trust what landed in the field: sanitizeInput reduces it to a
    // phone number's alphabet (digits, and a leading "+" for international
    // input) before anything else looks at it, so letters and stray
    // punctuation can't be typed, pasted or autofilled into the number.
    const { international, digits } = sanitizeInput(event.target.value);

    if (international) {
      const ayt = new AsYouType();
      ayt.input(`+${digits}`);
      const detected = ayt.getCountry();

      if (detected) {
        countryManuallySet.current = true;
        onCountryChange?.(detected);

        const capped = capNationalDigits(ayt.getNationalNumber(), detected);
        // Always correct the DOM by hand rather than relying on React's
        // re-render: React bails out of updating the DOM whenever `capped`
        // happens to equal what nationalDigits already was — which isn't
        // just the capped-to-the-same-length case, but also, e.g., "0" +
        // last time's digits normalizing to the same significant number as
        // last time's raw digits did, or a rejected character leaving the
        // digits untouched. Setting it here is never wrong: when React does
        // re-render, it computes and writes this exact string.
        event.target.value = formatNational(capped, detected);

        emit({ nextCountry: detected, nextNationalDigits: capped });
        return;
      }

      // Too short for libphonenumber-js to tell which country this is (just
      // "+3", say). The sanitized text is shown verbatim until it is —
      // there's nothing to split into country/national parts yet — but it's
      // the sanitized text, so it still can't hold anything but digits.
      const pending = `+${digits}`;
      event.target.value = pending;
      emit({ nextPending: pending });
      return;
    }

    // Fed the sanitized digits as typed (not the significant number) so a
    // national trunk prefix (e.g. Ukraine's leading "0") is recognized and
    // excluded from the E.164-ready digits this hands off to `emit`; the
    // display itself is always re-derived from those digits via
    // formatNational (see its comment for why that's needed for grouping).
    const ayt = new AsYouType(country);
    ayt.input(digits);
    const capped = capNationalDigits(ayt.getNationalNumber(), country);

    // Corrected by hand for the same reason as the international branch
    // above — React skips the DOM write whenever `capped` is unchanged,
    // which is exactly what happens when the keystroke was a rejected
    // character or a leading "0".
    event.target.value = formatNational(capped, country);

    emit({ nextNationalDigits: capped });
  }

  function handleCountrySelect(iso2) {
    countryManuallySet.current = true;
    onCountryChange?.(iso2);
    emit({ nextCountry: iso2 });
    numberInputRef.current?.focus();
  }

  function handleBlur(event) {
    setTouched(true);
    onBlur?.(event);
  }

  const callingCode = getCountryCallingCode(country);
  const digits = pendingInternational
    ? pendingInternational.replace(/\D/g, "")
    : `${callingCode}${nationalDigits}`;
  const internalError = touched
    ? getPhoneError({
        digits,
        nationalDigits: pendingInternational ? digits : nationalDigits,
        country,
        required,
      })
    : "";
  const errorMessage = error || internalError;
  const describedBy =
    [description && !errorMessage ? descId : null, errorMessage ? errorId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div
      className={styles.container + (className ? " " + className : "")}
      data-phone-input="root"
    >
      {label ? (
        <label className={styles.label} htmlFor={inputId}>
          {label}
          {required ? <span className={styles.required}>*</span> : null}
        </label>
      ) : null}

      <div
        className={[
          styles.control,
          grey ? styles.grey : "",
          errorMessage ? styles.controlError : "",
          disabled ? styles.controlDisabled : "",
        ]
          .filter(Boolean)
          .join(" ")}
        data-phone-input-control=""
      >
        <CountrySelect
          country={country}
          onSelect={handleCountrySelect}
          disabled={disabled || readOnly || loading}
          preferredCountries={preferredCountries}
          describedBy={describedBy}
        />
        <span className={styles.divider} aria-hidden="true" data-phone-input-divider="" />
        <input
          id={inputId}
          ref={numberInputRef}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          className={styles.numberInput}
          data-phone-input-number=""
          value={pendingInternational ?? formatNational(nationalDigits, country)}
          onChange={handleNumberChange}
          onBlur={handleBlur}
          onFocus={onFocus}
          placeholder={placeholder}
          disabled={disabled || loading}
          readOnly={readOnly}
          required={required}
          aria-invalid={Boolean(errorMessage)}
          aria-describedby={describedBy}
          aria-label={label ? undefined : "Номер телефону"}
        />
        {loading ? <span className={styles.spinner} aria-hidden="true" /> : null}
      </div>

      {description && !errorMessage ? (
        <small id={descId} className={styles.description}>
          {description}
        </small>
      ) : null}
      {errorMessage ? (
        <small id={errorId} role="alert" className={styles.error} data-phone-input-error="">
          {errorMessage}
        </small>
      ) : null}
    </div>
  );
}
